import { useEffect, useMemo, useState } from "react";
import {
  BookOpenText,
  ChevronDown,
  History,
  Pencil,
  Plus,
  Save,
  Search,
  Trash2,
} from "lucide-react";

import { Badge, Button } from "@/design-system/primitives";
import {
  type ClinicianTemplateInput,
  useArchiveClinicianTemplate,
  useClinicianDirectionHistory,
  useClinicianDirectionScopes,
  useClinicianProducts,
  useClinicianTemplates,
  useCreateClinicianTemplate,
  useUpdateClinicianTemplate,
} from "@/lib/api/clinicianHooks";
import type {
  ClinicianDirectionScope,
  ClinicianProduct,
  ClinicianTemplate,
  ClinicianTemplateKind,
} from "@/types/clinician";

const TABS: { value: ClinicianTemplateKind; label: string }[] = [
  { value: "direction", label: "Medication directions" },
  { value: "physician_note", label: "Physician notes" },
  { value: "message", label: "Patient messages" },
];

export function TemplatesScreen() {
  const [kind, setKind] = useState<ClinicianTemplateKind>("direction");
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(false);
  const templates = useClinicianTemplates();
  const directionScopes = useClinicianDirectionScopes();
  const products = useClinicianProducts();
  const productLabels = new Map(
    (products.data ?? []).map((product) => [product.product_id, product.label]),
  );
  const rows = (templates.data ?? []).filter(
    (template) => template.kind === kind,
  );
  const query = search.trim().toLocaleLowerCase();
  const matchesSearch = (template: ClinicianTemplate) =>
    !query ||
    [
      template.name,
      template.content,
      template.product_id,
      productLabels.get(template.product_id ?? ""),
      template.partner_key,
      template.decision,
      template.product_status,
    ].some((value) => value?.toLocaleLowerCase().includes(query));
  const matchedDirectionScopes = new Set(
    rows
      .filter(matchesSearch)
      .map(
        (template) =>
          `${template.partner_key ?? ""}:${template.product_id ?? ""}`,
      ),
  );
  const filtered = rows.filter((template) =>
    kind === "direction"
      ? matchedDirectionScopes.has(
          `${template.partner_key ?? ""}:${template.product_id ?? ""}`,
        )
      : matchesSearch(template),
  );
  const scopes = useMemo(() => {
    const labels = new Map(
      (products.data ?? []).map((product) => [
        product.product_id,
        product.label,
      ]),
    );
    const merged = new Map<string, ClinicianDirectionScope>();
    for (const template of templates.data ?? []) {
      if (template.kind !== "direction" || !template.product_id) continue;
      merged.set(`${template.partner_key ?? ""}:${template.product_id}`, {
        product_id: template.product_id,
        partner_key: template.partner_key ?? "",
        label: labels.get(template.product_id) ?? template.product_id,
        directions: template.content,
      });
    }
    for (const scope of directionScopes.data ?? []) {
      merged.set(`${scope.partner_key}:${scope.product_id}`, scope);
    }
    return Array.from(merged.values()).sort(
      (a, b) =>
        a.label.localeCompare(b.label) ||
        a.partner_key.localeCompare(b.partner_key),
    );
  }, [directionScopes.data, products.data, templates.data]);

  return (
    <div className="max-w-[100rem] mx-auto px-14 md:px-30 xl:px-40 py-24 md:py-40 flex flex-col gap-20">
      <header className="flex flex-wrap items-end justify-between gap-12">
        <div className="flex flex-col gap-5">
          <span className="type-label-sm text-ink-muted">
            TEMPLATES · SCRIPTS
          </span>
          <h1 className="type-heading-xl">Script templates</h1>
          <p className="type-body-sm text-ink-muted max-w-[64rem]">
            Maintain the reusable text offered during prescribing and patient
            messaging. Final notes, medications, and messages still require an
            explicit clinician action.
          </p>
        </div>
        <Button type="button" size="sm" onClick={() => setAdding(true)}>
          <Plus size={13} aria-hidden />
          {kind === "direction" ? "Add directions" : "New template"}
        </Button>
      </header>

      <div className="flex border-b border-line-subtle" role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={kind === tab.value}
            onClick={() => {
              setKind(tab.value);
              setAdding(false);
            }}
            className={`px-12 py-9 type-label-sm border-b-2 -mb-px ${
              kind === tab.value
                ? "border-ink text-ink"
                : "border-transparent text-ink-muted hover:text-ink"
            }`}
          >
            {tab.label}{" "}
            <span className="ml-4 opacity-60">
              {
                (templates.data ?? []).filter(
                  (entry) => entry.kind === tab.value,
                ).length
              }
            </span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-10">
        <label className="relative block w-full max-w-[42rem]">
          <Search
            size={14}
            className="absolute left-10 top-1/2 -translate-y-1/2 text-ink-muted"
            aria-hidden
          />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search templates"
            className="w-full min-h-38 pl-34 pr-10 border border-line bg-raised type-body-sm outline-none focus:shadow-focus"
          />
        </label>
        {(kind === "physician_note" || kind === "message") && (
          <p className="text-11 text-ink-muted">
            Supported fills: {"{{patient}}"}, {"{{product}}"},{" "}
            {"{{medication}}"}, {"{{clinician}}"} (the prescriber sending it, e.g.
            Stephen Yun, NP; filled when it is sent)
            {kind === "physician_note" ? (
              <>, {"{{rejection_reason}}"}</>
            ) : (
              <>
                , {"{{cancellation_summary}}"}, {"{{refund_summary}}"}
              </>
            )}
          </p>
        )}
      </div>

      {adding && (
        <NewTemplateForm
          kind={kind}
          scopes={scopes}
          products={products.data ?? []}
          onClose={() => setAdding(false)}
        />
      )}

      {templates.isLoading ? (
        <div className="grid md:grid-cols-2 gap-12 animate-pulse">
          <div className="h-[18rem] bg-sunken" />
          <div className="h-[18rem] bg-sunken" />
        </div>
      ) : templates.isError ? (
        <p className="border border-danger/30 bg-danger-wash p-14 type-body-sm text-danger">
          Templates could not be loaded. Nothing was changed.
        </p>
      ) : filtered.length ? (
        <TemplateRows
          templates={filtered}
          kind={kind}
          products={products.data ?? []}
        />
      ) : (
        <div className="border border-line-subtle p-24 flex flex-col items-center text-center gap-7">
          <BookOpenText size={20} className="text-ink-muted" />
          <p className="type-heading-sm">No templates in this view</p>
          <p className="type-body-sm text-ink-muted">
            Clear the search or create the first template.
          </p>
        </div>
      )}
    </div>
  );
}

function TemplateRows({
  templates,
  kind,
  products,
}: {
  templates: ClinicianTemplate[];
  kind: ClinicianTemplateKind;
  products: ClinicianProduct[];
}) {
  if (kind === "physician_note") {
    return (
      <DecisionTextTemplates
        templates={templates}
        products={products}
        kind="physician_note"
      />
    );
  }
  if (kind === "message") {
    return (
      <DecisionTextTemplates
        templates={templates}
        products={products}
        kind="message"
      />
    );
  }
  const sections = [
    {
      category: "treatment" as const,
      label: "Treatments",
      description: "Primary prescribed therapies",
    },
    {
      category: "ancillary" as const,
      label: "Ancillaries",
      description: "Supplies, kits, and supporting items",
    },
  ];
  return (
    <div className="flex flex-col gap-18">
      {sections.map((section) => {
        const sectionTemplates = templates.filter(
          (template) => template.product_category === section.category,
        );
        const grouped = sectionTemplates.reduce((result, template) => {
          const key = `${template.partner_key ?? ""}:${template.product_id ?? ""}`;
          result.set(key, [...(result.get(key) ?? []), template]);
          return result;
        }, new Map<string, ClinicianTemplate[]>());
        const groups = Array.from(grouped)
          .map(([scope, directionTemplates]) => ({
            scope,
            templates: directionTemplates.sort(
              (left, right) =>
                Number(right.is_default) - Number(left.is_default) ||
                left.sort_order - right.sort_order ||
                left.name.localeCompare(right.name),
            ),
          }))
          .sort((left, right) =>
            left.templates[0].name.localeCompare(right.templates[0].name),
          );
        return (
          <section
            key={section.category}
            className="border border-line-subtle bg-raised"
          >
            <div className="px-12 py-9 bg-sunken border-b border-line-subtle flex items-baseline justify-between gap-8">
              <div>
                <h2 className="type-heading-sm">{section.label}</h2>
                <p className="type-label-xs text-ink-muted">
                  {section.description}
                </p>
              </div>
              <span className="type-label-xs text-ink-muted">
                {groups.length} medication {groups.length === 1 ? "ID" : "IDs"}
              </span>
            </div>
            <div className="hidden md:grid grid-cols-[minmax(19rem,0.9fr)_minmax(26rem,2fr)_11rem_16rem] gap-9 px-11 py-7 border-b border-line-subtle type-label-xs text-ink-muted">
              <span>MEDICATION</span>
              <span>DEFAULT DIRECTIONS</span>
              <span>PHARMACY</span>
              <span />
            </div>
            {groups.length ? (
              <div className="divide-y divide-line-subtle">
                {groups.map((group) => (
                  <DirectionMedicationGroup
                    key={group.scope}
                    templates={group.templates}
                  />
                ))}
              </div>
            ) : (
              <p className="px-11 py-12 type-body-sm text-ink-muted">
                No ancillary direction templates yet.
              </p>
            )}
          </section>
        );
      })}
    </div>
  );
}

function DirectionMedicationGroup({
  templates,
}: {
  templates: ClinicianTemplate[];
}) {
  const first = templates[0];
  const defaultTemplate =
    templates.find((template) => template.is_default) ?? first;
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const create = useCreateClinicianTemplate();
  const history = useClinicianDirectionHistory(
    first.product_id,
    first.partner_key,
    historyOpen,
  );
  const [newName, setNewName] = useState("");
  const [newContent, setNewContent] = useState("");
  const [error, setError] = useState<string | null>(null);
  const medicationLabel = defaultTemplate.name.replace(
    /\s+—\s+(Standard|Default|Option \d+)$/,
    "",
  );

  const addDirections = async () => {
    setError(null);
    try {
      await create.mutateAsync({
        kind: "direction",
        name: newName.trim(),
        content: newContent.trim(),
        product_id: first.product_id,
        partner_key: first.partner_key,
        is_default: false,
      });
      setNewName("");
      setNewContent("");
      setAdding(false);
      setOpen(true);
    } catch {
      setError("Directions could not be added.");
    }
  };

  return (
    <article>
      <div className="grid md:grid-cols-[minmax(19rem,0.9fr)_minmax(26rem,2fr)_11rem_16rem] gap-5 md:gap-9 items-center px-11 py-9">
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="min-w-0 text-left focus-visible:outline-none focus-visible:shadow-focus"
          aria-expanded={open}
        >
          <p className="type-body-sm font-medium truncate">{medicationLabel}</p>
          <div className="mt-2 flex flex-wrap items-center gap-5">
            <p className="type-label-xs text-ink-muted truncate">
              {first.product_id}
            </p>
            <ProductAvailabilityBadge template={first} />
          </div>
        </button>
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="min-w-0 text-left focus-visible:outline-none focus-visible:shadow-focus"
          aria-expanded={open}
        >
          <span className="type-label-xs text-accent">DEFAULT</span>
          <p className="type-body-sm text-ink-secondary line-clamp-2 mt-2">
            {defaultTemplate.content}
          </p>
        </button>
        <span className="type-label-xs text-ink-muted truncate">
          {first.partner_key || "Unassigned"}
        </span>
        <div className="flex items-center justify-end gap-6">
          <button
            type="button"
            onClick={() => setHistoryOpen((value) => !value)}
            className="inline-flex items-center gap-4 type-label-xs text-ink-muted hover:text-ink"
          >
            <History size={13} /> History
          </button>
          <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            className="inline-flex items-center gap-4 type-label-xs"
          >
            <ChevronDown
              size={13}
              className={open ? "rotate-180 transition-transform" : undefined}
            />
            {templates.length} {templates.length === 1 ? "option" : "options"}
          </button>
        </div>
      </div>

      {historyOpen && (
        <DirectionHistoryPanel
          loading={history.isLoading}
          entries={history.data ?? []}
        />
      )}

      {open && (
        <div className="border-t border-line-subtle bg-sunken/40 px-11 py-10 flex flex-col gap-8">
          <div className="flex items-center justify-between gap-8">
            <div>
              <p className="type-label-sm">Direction templates</p>
              <p className="text-11 text-ink-muted mt-2">
                One default is always required. Direction templates cannot be
                archived.
              </p>
            </div>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => setAdding(true)}
            >
              <Plus size={12} /> Add directions
            </Button>
          </div>
          <div className="border border-line-subtle divide-y divide-line-subtle bg-raised">
            {templates.map((template) => (
              <DirectionTemplateRow key={template.id} template={template} />
            ))}
          </div>
          {adding && (
            <div className="border border-ink bg-raised p-10 grid md:grid-cols-[minmax(14rem,0.7fr)_minmax(0,2fr)] gap-8">
              <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
                TEMPLATE LABEL
                <input
                  value={newName}
                  onChange={(event) => setNewName(event.target.value)}
                  placeholder="e.g. Lower starting dose"
                  className="min-h-38 border border-line px-9 type-body-sm text-ink outline-none focus:shadow-focus"
                />
              </label>
              <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
                DIRECTIONS
                <textarea
                  value={newContent}
                  onChange={(event) => setNewContent(event.target.value)}
                  rows={3}
                  className="border border-line px-9 py-8 type-body-sm text-ink outline-none resize-y focus:shadow-focus"
                />
              </label>
              <div className="md:col-span-2 flex items-center justify-between gap-8">
                <span className="text-11 text-danger">{error}</span>
                <div className="flex gap-6">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setAdding(false);
                      setError(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    loading={create.isPending}
                    disabled={!newName.trim() || !newContent.trim()}
                    onClick={addDirections}
                  >
                    Add template
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </article>
  );
}

function DirectionTemplateRow({ template }: { template: ClinicianTemplate }) {
  const update = useUpdateClinicianTemplate();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(template.name);
  const [content, setContent] = useState(template.content);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    setName(template.name);
    setContent(template.content);
  }, [template.content, template.name]);

  const save = async () => {
    setMessage(null);
    try {
      await update.mutateAsync({
        id: template.id,
        name: name.trim(),
        content: content.trim(),
      });
      setMessage("Saved");
      setEditing(false);
    } catch {
      setMessage("Could not save");
    }
  };

  return (
    <div>
      <div className="grid md:grid-cols-[minmax(15rem,0.7fr)_minmax(0,2fr)_auto] gap-6 md:gap-9 items-center px-10 py-8">
        <div className="min-w-0 flex items-center gap-6">
          <p className="type-body-sm font-medium truncate">{template.name}</p>
          {template.is_default && (
            <span className="shrink-0 px-5 py-2 bg-accent-wash type-label-xs text-accent">
              DEFAULT
            </span>
          )}
        </div>
        <p className="type-body-sm text-ink-secondary line-clamp-2">
          {template.content}
        </p>
        <div className="flex items-center justify-end gap-6">
          {!template.is_default && (
            <button
              type="button"
              onClick={() =>
                update.mutate({ id: template.id, is_default: true })
              }
              className="type-label-xs text-ink-muted hover:text-ink"
            >
              Make default
            </button>
          )}
          <button
            type="button"
            onClick={() => setEditing((value) => !value)}
            className="inline-flex items-center gap-4 type-label-xs"
          >
            <Pencil size={12} /> Edit
          </button>
        </div>
      </div>
      {editing && (
        <div className="border-t border-line-subtle bg-sunken/50 px-10 py-9 grid md:grid-cols-[minmax(14rem,0.7fr)_minmax(0,2fr)] gap-9">
          <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
            TEMPLATE LABEL
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              className="min-h-38 border border-line bg-raised px-8 type-body-sm text-ink outline-none focus:shadow-focus"
            />
          </label>
          <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
            DIRECTIONS
            <textarea
              value={content}
              onChange={(event) => setContent(event.target.value)}
              rows={4}
              className="border border-line bg-raised px-9 py-8 type-body-sm text-ink outline-none resize-y focus:shadow-focus"
            />
          </label>
          <div className="md:col-span-2 flex items-center justify-between gap-8">
            <span
              className={`text-11 ${message === "Saved" ? "text-success" : "text-danger"}`}
            >
              {message}
            </span>
            <div className="flex items-center gap-6">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setEditing(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                loading={update.isPending}
                disabled={!name.trim() || !content.trim()}
                onClick={save}
              >
                <Save size={12} /> Save directions
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ProductAvailabilityBadge({
  template,
}: {
  template: ClinicianTemplate;
}) {
  const missing = [
    !template.catalog_active && "active pharmacy catalog item",
    !template.pharmacy_active && "active pharmacy partner",
    !template.route_active && "routing rule",
    !template.medication_mapped && "prescription medication mapping",
  ].filter(Boolean) as string[];
  const status = {
    shop_live: {
      label: "SHOP LIVE",
      tone: "success" as const,
      detail:
        "Available in the production patient Shop, directly or through its treatment program, and ready to route to this pharmacy.",
    },
    configured: {
      label: "NOT IN SHOP",
      tone: "neutral" as const,
      detail:
        "Pharmacy catalog, partner, routing, and medication mapping are active, but this medication ID is not listed in the production patient Shop.",
    },
    setup_required: {
      label: "SETUP ISSUE",
      tone: "warning" as const,
      detail: `Listed in the production patient Shop, but not currently fulfillable. Missing: ${missing.join(", ") || "fulfillment configuration"}.`,
    },
    not_live: {
      label: "NOT LIVE",
      tone: "neutral" as const,
      detail: `Not listed in the production patient Shop and not fully configured for fulfillment${missing.length ? `. Missing: ${missing.join(", ")}.` : "."}`,
    },
    unknown: {
      label: "STATUS UNKNOWN",
      tone: "neutral" as const,
      detail:
        "The production Shop catalog could not be checked. No live/not-live assumption was made.",
    },
  }[template.product_status];

  return (
    <Badge
      tone={status.tone}
      size="sm"
      title={status.detail}
      aria-label={`${status.label}. ${status.detail}`}
    >
      {status.label}
    </Badge>
  );
}

function DirectionHistoryPanel({
  loading,
  entries,
}: {
  loading: boolean;
  entries: import("@/types/clinician").ClinicianTemplateHistory[];
}) {
  const labels: Record<string, string> = {
    template_added: "Direction template added",
    template_edited: "Directions edited",
    template_removed: "Direction template removed",
    default_changed: "Default changed",
  };
  return (
    <div className="border-t border-line-subtle bg-raised px-11 py-10">
      <p className="type-label-sm mb-7">Direction history</p>
      {loading ? (
        <p className="type-body-sm text-ink-muted">Loading history…</p>
      ) : entries.length ? (
        <ol className="flex flex-col gap-7">
          {entries.map((entry) => {
            const state = entry.new_value ?? entry.previous_value;
            const name = typeof state?.name === "string" ? state.name : null;
            const previousContent =
              typeof entry.previous_value?.content === "string"
                ? entry.previous_value.content
                : null;
            const newContent =
              typeof entry.new_value?.content === "string"
                ? entry.new_value.content
                : null;
            return (
              <li
                key={entry.id}
                className="grid md:grid-cols-[minmax(14rem,0.6fr)_minmax(0,1fr)_auto] gap-4 md:gap-8 type-body-sm"
              >
                <span className="font-medium">{labels[entry.action]}</span>
                <div className="min-w-0">
                  <p className="text-ink-secondary truncate">
                    {name || "Medication directions"} · {entry.actor}
                  </p>
                  {entry.action === "template_edited" &&
                    previousContent !== newContent && (
                      <p className="text-11 text-ink-muted mt-2 line-clamp-2">
                        “{previousContent}” → “{newContent}”
                      </p>
                    )}
                  {entry.action === "template_added" && newContent && (
                    <p className="text-11 text-ink-muted mt-2 line-clamp-2">
                      {newContent}
                    </p>
                  )}
                </div>
                <time className="text-11 text-ink-muted">
                  {new Date(entry.created_at).toLocaleString()}
                </time>
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="type-body-sm text-ink-muted">
          No changes have been recorded since history was enabled.
        </p>
      )}
    </div>
  );
}

function DecisionTextTemplates({
  templates,
  products,
  kind,
}: {
  templates: ClinicianTemplate[];
  products: ClinicianProduct[];
  kind: "physician_note" | "message";
}) {
  const productLabels = new Map(
    products.map((product) => [product.product_id, product.label]),
  );
  const sections = [
    {
      decision: "approve" as const,
      label: kind === "physician_note" ? "Approval notes" : "Approval messages",
      description:
        kind === "physician_note"
          ? "Your personal default wins, then the product default, then the global approval default. Personal templates are offered only to you."
          : "Sent only after the note and medication record succeed. The clinician can switch or edit this copy during prescribing.",
    },
    {
      decision: "reject" as const,
      label:
        kind === "physician_note" ? "Rejection notes" : "Rejection messages",
      description:
        kind === "physician_note"
          ? "The clinician adds a required rejection reason during the guided decision flow."
          : "Sent after cancellation and refund finish, so result placeholders can describe what actually happened.",
    },
  ];
  return (
    <div className="flex flex-col gap-16">
      {sections.map((section) => {
        const rows = templates
          .filter((template) => template.decision === section.decision)
          .sort(
            (left, right) =>
              Number(Boolean(left.product_id)) -
                Number(Boolean(right.product_id)) ||
              Number(right.is_default) - Number(left.is_default) ||
              (
                productLabels.get(left.product_id ?? "") ?? left.name
              ).localeCompare(
                productLabels.get(right.product_id ?? "") ?? right.name,
              ),
          );
        return (
          <section
            key={section.decision}
            className="border border-line-subtle bg-raised"
          >
            <div className="px-12 py-9 bg-sunken border-b border-line-subtle flex flex-wrap items-start justify-between gap-8">
              <div>
                <h2 className="type-heading-sm">{section.label}</h2>
                <p className="text-11 text-ink-muted mt-2">
                  {section.description}
                </p>
              </div>
              <Badge
                tone={section.decision === "approve" ? "success" : "danger"}
                size="sm"
              >
                {rows.length} {rows.length === 1 ? "template" : "templates"}
              </Badge>
            </div>
            {rows.length ? (
              <div className="divide-y divide-line-subtle">
                {rows.map((template) => (
                  <DecisionTextTemplateRow
                    key={template.id}
                    template={template}
                    kind={kind}
                    productLabel={
                      template.product_id
                        ? (productLabels.get(template.product_id) ??
                          template.product_id)
                        : null
                    }
                  />
                ))}
              </div>
            ) : (
              <p className="px-12 py-10 type-body-sm text-ink-muted">
                No {section.label.toLocaleLowerCase()} match this search.
              </p>
            )}
          </section>
        );
      })}
    </div>
  );
}

function DecisionTextTemplateRow({
  template,
  productLabel,
  kind,
}: {
  template: ClinicianTemplate;
  productLabel: string | null;
  kind: "physician_note" | "message";
}) {
  const update = useUpdateClinicianTemplate();
  const archive = useArchiveClinicianTemplate();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(template.name);
  const [content, setContent] = useState(template.content);
  const [message, setMessage] = useState<string | null>(null);
  // Personal templates are offered only to their owner; "default" on one
  // makes it that clinician's own default, never the shared one.
  const personal = Boolean(template.owner_clinician_id);
  const isDefault = personal ? Boolean(template.owner_default) : template.is_default;

  useEffect(() => {
    setName(template.name);
    setContent(template.content);
  }, [template.content, template.name]);

  const save = async () => {
    setMessage(null);
    try {
      await update.mutateAsync({
        id: template.id,
        name: name.trim(),
        content: content.trim(),
      });
      setMessage("Saved");
      setEditing(false);
    } catch {
      setMessage("Could not save");
    }
  };

  return (
    <article>
      <div className="grid md:grid-cols-[minmax(14rem,0.7fr)_minmax(0,1.7fr)_auto] items-center gap-7 md:gap-10 px-11 py-9">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-5">
            <p className="type-body-sm font-medium truncate">{template.name}</p>
            {template.is_default && (
              <span className="px-5 py-2 bg-accent-wash type-label-xs text-accent">
                DEFAULT
              </span>
            )}
            {personal && (
              <span className="px-5 py-2 border border-line type-label-xs text-ink-muted">
                {template.owner_default ? "MY DEFAULT" : "MINE"}
              </span>
            )}
          </div>
          <p className="type-label-xs text-ink-muted mt-3 truncate">
            {productLabel ? `PRODUCT · ${productLabel}` : "GLOBAL FALLBACK"}
          </p>
        </div>
        <p className="type-body-sm text-ink-secondary line-clamp-2 whitespace-pre-wrap">
          {template.content}
        </p>
        <div className="flex flex-wrap items-center justify-end gap-6">
          {!isDefault && (
            <button
              type="button"
              onClick={async () => {
                setMessage(null);
                try {
                  await update.mutateAsync({
                    id: template.id,
                    is_default: true,
                  });
                  setMessage("Default updated");
                } catch {
                  setMessage("Could not set default");
                }
              }}
              className="type-label-xs text-ink-muted hover:text-ink"
            >
              {personal ? "Make my default" : "Make default"}
            </button>
          )}
          <button
            type="button"
            onClick={() => setEditing((value) => !value)}
            className="inline-flex items-center gap-4 type-label-xs"
          >
            <Pencil size={12} /> Edit
          </button>
        </div>
      </div>

      {editing && (
        <div className="border-t border-line-subtle bg-sunken/50 px-11 py-10 grid md:grid-cols-[minmax(14rem,0.7fr)_minmax(0,2fr)] gap-9">
          <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
            TEMPLATE NAME
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              className="min-h-38 border border-line bg-raised px-9 type-body-sm text-ink outline-none focus:shadow-focus"
            />
          </label>
          <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
            {kind === "physician_note" ? "NOTE CONTENT" : "PATIENT MESSAGE"}
            <textarea
              value={content}
              onChange={(event) => setContent(event.target.value)}
              rows={5}
              className="border border-line bg-raised px-9 py-8 type-body-sm text-ink outline-none resize-y focus:shadow-focus"
            />
          </label>
          <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-8">
            <span
              className={`text-11 ${message?.includes("Could not") ? "text-danger" : "text-success"}`}
            >
              {message}
            </span>
            <div className="flex items-center gap-6">
              {!template.is_default && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  loading={archive.isPending}
                  onClick={() => {
                    if (window.confirm(`Archive “${template.name}”?`)) {
                      archive.mutate(template.id);
                    }
                  }}
                >
                  <Trash2 size={12} /> Archive
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setEditing(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                loading={update.isPending}
                disabled={!name.trim() || !content.trim()}
                onClick={save}
              >
                <Save size={12} /> Save{" "}
                {kind === "physician_note" ? "note" : "message"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </article>
  );
}

function NewTemplateForm({
  kind,
  scopes,
  products,
  onClose,
}: {
  kind: ClinicianTemplateKind;
  scopes: ClinicianDirectionScope[];
  products: ClinicianProduct[];
  onClose: () => void;
}) {
  const create = useCreateClinicianTemplate();
  const initialScope = scopes[0];
  const [name, setName] = useState(
    kind === "direction" && initialScope
      ? `${initialScope.label} — Standard`
      : "",
  );
  const [content, setContent] = useState(
    kind === "direction" ? (initialScope?.directions ?? "") : "",
  );
  const [scope, setScope] = useState(
    initialScope
      ? `${initialScope.partner_key}:${initialScope.product_id}`
      : "",
  );
  const [decision, setDecision] = useState<"approve" | "reject">("approve");
  const [noteProductId, setNoteProductId] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [personal, setPersonal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (kind !== "direction" || scope || !scopes[0]) return;
    const first = scopes[0];
    setScope(`${first.partner_key}:${first.product_id}`);
    setName(`${first.label} — Standard`);
    setContent(first.directions);
  }, [kind, scope, scopes]);

  const changeScope = (value: string) => {
    const previous = scopes.find(
      (entry) => `${entry.partner_key}:${entry.product_id}` === scope,
    );
    const next = scopes.find(
      (entry) => `${entry.partner_key}:${entry.product_id}` === value,
    );
    setScope(value);
    if (!next) return;
    if (!name.trim() || name === `${previous?.label} — Standard`) {
      setName(`${next.label} — Standard`);
    }
    if (!content.trim() || content === previous?.directions) {
      setContent(next.directions);
    }
  };

  const submit = async () => {
    const selected = scopes.find(
      (entry) => `${entry.partner_key ?? ""}:${entry.product_id}` === scope,
    );
    const body: ClinicianTemplateInput = {
      kind,
      name: name.trim(),
      content: content.trim(),
      ...(kind === "direction"
        ? {
            product_id: selected?.product_id,
            partner_key: selected?.partner_key,
          }
        : {}),
      ...(kind === "physician_note" || kind === "message"
        ? {
            decision,
            ...(kind === "physician_note" || kind === "message"
              ? {
                  product_id: noteProductId || undefined,
                  is_default: isDefault,
                  personal,
                }
              : {}),
          }
        : {}),
    };
    setError(null);
    try {
      await create.mutateAsync(body);
      onClose();
    } catch {
      setError("Template could not be created.");
    }
  };

  return (
    <section className="border border-ink bg-raised p-12 flex flex-col gap-9">
      <div className="flex items-center justify-between gap-8">
        <h2 className="type-label-sm font-medium">New template</h2>
        <button
          type="button"
          onClick={onClose}
          className="type-label-xs text-ink-muted"
        >
          Cancel
        </button>
      </div>
      <div className="grid md:grid-cols-3 gap-8">
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Template name"
          className="min-h-38 border border-line px-9 type-body-sm outline-none focus:shadow-focus"
        />
        {kind === "direction" && (
          <select
            value={scope}
            onChange={(event) => changeScope(event.target.value)}
            className="min-h-38 border border-line px-9 type-body-sm"
          >
            {scopes.map((entry) => (
              <option
                key={`${entry.partner_key}:${entry.product_id}`}
                value={`${entry.partner_key}:${entry.product_id}`}
              >
                {entry.label} · {entry.partner_key}
              </option>
            ))}
          </select>
        )}
        {(kind === "physician_note" || kind === "message") && (
          <select
            value={decision}
            onChange={(event) =>
              setDecision(event.target.value as "approve" | "reject")
            }
            className="min-h-38 border border-line px-9 type-body-sm"
          >
            <option value="approve">Approval</option>
            <option value="reject">Rejection</option>
          </select>
        )}
        {(kind === "physician_note" || kind === "message") && (
          <select
            value={noteProductId}
            onChange={(event) => setNoteProductId(event.target.value)}
            className="min-h-38 border border-line px-9 type-body-sm"
            aria-label={`${kind === "physician_note" ? "Physician note" : "Patient message"} product scope`}
          >
            <option value="">Global fallback · all products</option>
            {[...products]
              .sort((left, right) => left.label.localeCompare(right.label))
              .map((product) => (
                <option key={product.product_id} value={product.product_id}>
                  Product · {product.label}
                </option>
              ))}
          </select>
        )}
      </div>
      <textarea
        value={content}
        onChange={(event) => setContent(event.target.value)}
        rows={5}
        placeholder="Reusable text"
        className="w-full border border-line bg-sunken px-9 py-8 type-body-sm outline-none focus:shadow-focus"
      />
      <div className="flex items-center justify-between gap-8">
        <div className="flex flex-col gap-3">
          <span className="text-11 text-danger">{error}</span>
          {(kind === "physician_note" || kind === "message") && (
            <label className="inline-flex items-center gap-5 type-body-sm text-ink-secondary">
              <input
                type="checkbox"
                checked={personal}
                onChange={(event) => setPersonal(event.target.checked)}
              />
              Personal: only offered to me
            </label>
          )}
          {(kind === "physician_note" || kind === "message") && (
            <label className="inline-flex items-center gap-5 type-body-sm text-ink-secondary">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(event) => setIsDefault(event.target.checked)}
              />
              {personal
                ? "Make this my default for the selected decision and scope"
                : "Make this the default for the selected decision and scope"}
            </label>
          )}
        </div>
        <Button
          type="button"
          size="sm"
          loading={create.isPending}
          disabled={
            !name.trim() || !content.trim() || (kind === "direction" && !scope)
          }
          onClick={submit}
        >
          <Plus size={12} /> Create template
        </Button>
      </div>
    </section>
  );
}
